import { ContentListShell } from "@/components/studio/content-list-shell";
import { LifecycleActionsMenu } from "@/components/studio/lifecycle-actions-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useArchiveAudio,
  useAudioList,
  useRestoreAudio,
  useSubmitAudio,
} from "@/hooks/useAudio";
import { useCategories } from "@/hooks/useCategories";
import { useCoaches } from "@/hooks/useCoaches";
import { ContentTagService } from "@/lib/api/content.tag.service";
import { useAuth } from "@/lib/auth";
import type { StudioContentStatus, TagGroup, TagOption } from "@/lib/schema";
import {
  CONTENT_TYPE_CONFIG,
  formatDate,
  STATUS_CONFIG,
} from "@/lib/studio-constants";
import { useQuery } from "@tanstack/react-query";
import { Globe, Headphones, Tags, User } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";

const audioConfig = CONTENT_TYPE_CONFIG.audio;
const PAGE_SIZE = 5;
const ELEVATED_ROLES = [
  "admin",
  "super_admin",
  "coach_manager",
  "content_manager",
];

export default function AudioListPage() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const isCoachOnly =
    !!user?.roles?.includes("coach") &&
    !user.roles.some((r) => ELEVATED_ROLES.includes(r));
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterValues, setFilterValues] = useState<Record<string, string>>({
    status: "all",
    category: "all",
    tag: "all",
    coach: "all",
  });
  const [languageFilter, setLanguageFilter] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, filterValues, languageFilter]);

  const queryParams = useMemo(
    () => ({
      search: searchQuery || undefined,
      status:
        filterValues.status !== "all"
          ? (filterValues.status as StudioContentStatus)
          : undefined,
      categoryId:
        filterValues.category !== "all" ? filterValues.category : undefined,
      tagId: filterValues.tag !== "all" ? filterValues.tag : undefined,
      coachId: filterValues.coach !== "all" ? filterValues.coach : undefined,
      language: languageFilter !== "all" ? languageFilter : undefined,
      page,
      limit: PAGE_SIZE,
    }),
    [searchQuery, filterValues, languageFilter, page],
  );

  const { data: audioResponse, isLoading } = useAudioList(queryParams);
  const audioItems = audioResponse?.items ?? [];
  const totalItems = audioResponse?.pagination.total ?? 0;
  const totalPages = audioResponse?.pagination.totalPages ?? 1;
  const { data: categories = [] } = useCategories();
  const { data: coachesData } = useCoaches({ enabled: !isCoachOnly });
  const coaches = coachesData?.coaches ?? [];

  const { data: tagGroups = [] } = useQuery<TagGroup[]>({
    queryKey: ["/content/tag-groups"],
    queryFn: () => ContentTagService.getTagGroups(),
  });

  const { data: tagOptions = [] } = useQuery<TagOption[]>({
    queryKey: ["/content/tag-options"],
    queryFn: () => ContentTagService.getTagOptions(),
  });

  const submitMutation = useSubmitAudio();
  const archiveMutation = useArchiveAudio();
  const restoreMutation = useRestoreAudio();

  const tagOptionsWithGroup = tagOptions.map((opt) => {
    const group = tagGroups.find((g) => g.id === opt.groupId);
    return { ...opt, groupName: group?.name ?? "" };
  });

  const handleFilterChange = (key: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const customFilters = [
    {
      key: "category",
      label: "Categories",
      options: categories.map((cat) => ({ value: cat.id, label: cat.title })),
    },
    {
      key: "tag",
      label: "Tags",
      options: tagOptionsWithGroup.map((opt) => ({
        value: opt.id,
        label: opt.groupName ? `${opt.groupName}: ${opt.label}` : opt.label,
      })),
    },
    ...(isCoachOnly
      ? []
      : [
          {
            key: "coach",
            label: "Coaches",
            width: "160px",
            options: coaches.map((coach) => ({
              value: coach.id,
              label: coach.name,
            })),
          },
        ]),
  ];

  const startIndex = totalItems === 0 ? 0 : (page - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + audioItems.length, totalItems);

  return (
    <ContentListShell
      contentTypeConfig={audioConfig}
      isLoading={isLoading}
      itemCount={totalItems}
      searchQuery={searchInput}
      onSearchChange={setSearchInput}
      filters={customFilters}
      filterValues={filterValues}
      onFilterChange={handleFilterChange}
      showLanguageFilter={true}
      languageFilterValue={languageFilter}
      onLanguageFilterChange={setLanguageFilter}
      pagination={{
        currentPage: page,
        totalPages,
        totalItems,
        startIndex,
        endIndex,
        canGoNext: page < totalPages,
        canGoPrev: page > 1,
        onPageChange: setPage,
        onNext: () => setPage((p) => Math.min(p + 1, totalPages)),
        onPrev: () => setPage((p) => Math.max(p - 1, 1)),
      }}
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[300px]">Title</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Author</TableHead>
            <TableHead className="text-center">Tags</TableHead>
            <TableHead className="text-center">Languages</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Updated</TableHead>
            <TableHead className="w-[50px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {audioItems.map((item) => {
            const statusConfig =
              STATUS_CONFIG[item.status] || STATUS_CONFIG.draft;
            return (
              <TableRow
                key={item.versionId}
                className="cursor-pointer"
                onClick={() =>
                  setLocation(`/studio/audio/version/${item.versionId}/edit`)
                }
              >
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-md bg-muted shrink-0">
                      <Headphones className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-foreground truncate">
                        {item.primaryTitle}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        v{item.version}
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">
                    {categories.find((c) => c.id === item.categoryId)?.title ||
                      "\u2014"}
                  </span>
                </TableCell>
                <TableCell>
                  {(() => {
                    const validCoaches =
                      item.coaches?.filter((c) => !!c.name) ?? [];
                    if (validCoaches.length === 0) {
                      return (
                        <div className="flex items-center gap-1.5">
                          <User className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            {"\u2014"}
                          </span>
                        </div>
                      );
                    }
                    const first = validCoaches[0];
                    const label =
                      validCoaches.length === 1
                        ? first.name
                        : `${first.name} +${validCoaches.length - 1}`;
                    return (
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6">
                          {first.avatarUrl ? (
                            <AvatarImage
                              src={first.avatarUrl}
                              alt={first.name ?? ""}
                            />
                          ) : null}
                          <AvatarFallback className="text-[10px]">
                            {first.name?.charAt(0).toUpperCase() ?? "?"}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm text-muted-foreground">
                          {label}
                        </span>
                      </div>
                    );
                  })()}
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Tags className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {item.tagOptionIds?.length ?? 0}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {item.languageCodes?.length ?? 0}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={statusConfig.variant}>
                    {statusConfig.label}
                  </Badge>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">
                    {formatDate(item.updatedAt)}
                  </span>
                </TableCell>
                <TableCell>
                  <LifecycleActionsMenu
                    itemId={item.id}
                    status={item.status}
                    createdBy={item.creator?.id}
                    authorCoachId={
                      user && item.coachIds?.includes(user.id)
                        ? user.id
                        : undefined
                    }
                    onEdit={() =>
                      setLocation(
                        `/studio/audio/version/${item.versionId}/edit`,
                      )
                    }
                    onSubmit={(audioId) => submitMutation.mutate(audioId)}
                    onArchive={(audioId) => archiveMutation.mutate(audioId)}
                    onRestore={(audioId) => restoreMutation.mutate(audioId)}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </ContentListShell>
  );
}
